import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { useTheme } from '../context/ThemeContext.jsx';
import { usePremium } from '../context/PremiumContext.jsx';
import PremiumCrown from '../components/PremiumCrown.jsx';
import api from '../api.js';

// Curated set of on-brand emoji avatars. Users can pick one in edit mode.
const AVATAR_OPTIONS = [
  '⛳', '🍺', '🍻', '🥃', '🍸', '🍹', '🥂', '🍷',
  '🏆', '👑', '🎯', '🎖️', '🎱', '🕶️', '🎩', '🧢',
  '🦁', '🐻', '🦊', '🐯', '🦉', '🐸', '🐼', '🐵',
];

/** Is this avatar an uploaded image URL rather than an emoji? */
function isImageAvatar(v) {
  if (!v || typeof v !== 'string') return false;
  return v.startsWith('http://') || v.startsWith('https://') || v.startsWith('/');
}

export default function Profile() {
  const { user, signOut, updateUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const { premium, unlimited } = usePremium();
  const tier = unlimited ? 'unlimited' : premium ? 'premium' : 'free';
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.displayName || '');
  const [city, setCity] = useState(user?.city || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [avatar, setAvatar] = useState(user?.avatar || '⛳');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  useEffect(() => {
    if (!user?.uid) {
      setLoadingStats(false);
      return;
    }
    setLoadingStats(true);
    api
      .getProfileStats(user.uid)
      .then(setStats)
      .catch(() => {})
      .finally(() => setLoadingStats(false));
    // Also hydrate the profile from the server so fresh data replaces
    // whatever the auth blob had.
    api.getProfile(user.uid).then((p) => {
      if (p?.bio) setBio(p.bio);
      if (p?.avatar) setAvatar(p.avatar);
      if (p?.city) setCity(p.city);
      if (p?.display_name) setName(p.display_name);
    }).catch(() => {});
  }, [user?.uid]);

  const save = async () => {
    try {
      await api.updateProfile({
        userId: user.uid,
        displayName: name,
        city,
        bio,
        avatar,
      });
    } catch {}
    updateUser({ displayName: name, city, bio, avatar });
    setEditing(false);
  };

  const handleSignOut = () => {
    signOut();
    navigate('/login');
  };

  /**
   * Upload a photo from the user's device and set it as their avatar.
   * Reuses /api/stories since it already handles multipart image uploads
   * and stores files in /uploads/. The returned imageUrl is persisted
   * into the `profile.avatar` field (TEXT — accepts either an emoji or
   * a URL).
   */
  const uploadAvatar = async (file) => {
    if (!file || !user?.uid) return;
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      fd.append('userId', user.uid);
      fd.append('caption', '[avatar]');
      // Mark it so the story feed can filter these out if desired.
      fd.append('avatarUpload', '1');
      const res = await api.postStory(fd);
      const url = res?.imageUrl || res?.image_url;
      if (url) setAvatar(url);
    } catch (err) {
      console.warn('avatar upload failed', err);
    } finally {
      setUploadingAvatar(false);
    }
  };

  return (
    <div className="screen screen-narrow">
      <div className="profile-header">
        <div className={'avatar ' + (isImageAvatar(avatar) ? 'avatar-photo' : 'avatar-emoji')}>
          {isImageAvatar(avatar) ? (
            <img src={avatar} alt={name || 'Avatar'} />
          ) : (
            avatar || '🙂'
          )}
        </div>
        {editing ? (
          <div className="stack-sm" style={{ width: '100%' }}>
            <label className="avatar-upload-btn">
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => uploadAvatar(e.target.files?.[0])}
              />
              {uploadingAvatar ? 'Uploading…' : '📷 Upload photo'}
            </label>
            <div className="avatar-picker">
              {AVATAR_OPTIONS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  className={'avatar-option' + (avatar === emoji ? ' active' : '')}
                  onClick={() => setAvatar(emoji)}
                  aria-label={`Pick ${emoji} avatar`}
                >
                  {emoji}
                </button>
              ))}
            </div>
            <input
              className="input"
              value={name}
              placeholder="Display name"
              onChange={(e) => setName(e.target.value)}
            />
            <input
              className="input"
              value={city}
              placeholder="City"
              onChange={(e) => setCity(e.target.value)}
            />
            <textarea
              className="input"
              value={bio}
              placeholder="Bio (one line about your golf game)"
              rows={2}
              maxLength={160}
              onChange={(e) => setBio(e.target.value)}
              style={{ resize: 'vertical', minHeight: '3rem' }}
            />
            <div className="row" style={{ gap: '0.5rem' }}>
              <button
                className="btn btn-ghost grow"
                onClick={() => {
                  setEditing(false);
                  setName(user?.displayName || '');
                  setCity(user?.city || '');
                  setBio(user?.bio || '');
                  setAvatar(user?.avatar || '⛳');
                }}
              >
                Cancel
              </button>
              <button className="btn btn-primary grow" onClick={save}>
                Save
              </button>
            </div>
          </div>
        ) : (
          <>
            <h1>
              {name || user?.displayName || 'Guest'}
              <PremiumCrown tier={tier} size="md" />
            </h1>
            <div className="city">{city || 'Nowhere in particular'}</div>
            {bio && <div className="profile-bio">{bio}</div>}
            <button
              className="chip"
              style={{ marginTop: '0.75rem' }}
              onClick={() => setEditing(true)}
            >
              ✎ Edit Profile
            </button>
          </>
        )}
      </div>

      <div className="stats-grid">
        <div className="stat-tile games">
          <span className="icon">⛳</span>
          <div className="num">{stats?.gamesPlayed ?? 0}</div>
          <div className="label">Rounds</div>
        </div>
        <div className="stat-tile wins">
          <span className="icon">🏆</span>
          <div className="num">{stats?.wins ?? 0}</div>
          <div className="label">Wins</div>
        </div>
        <div className="stat-tile best">
          <span className="icon">⚡</span>
          <div className="num">{stats?.bestScore ?? '—'}</div>
          <div className="label">Best</div>
        </div>
      </div>

      {/* Extended stats list — only shows if the user has played at least
          one round. Each tile here uses the neutral .card style since the
          glow tiles above are the hero stats. */}
      {stats && (stats.gamesPlayed || 0) > 0 && (
        <>
          <div className="u-label" style={{ marginBottom: '0.75rem' }}>
            Career Stats
          </div>
          <div className="stat-list">
            {stats.losses != null && (
              <StatRow icon="💀" label="Losses" value={stats.losses} />
            )}
            {stats.worstScore != null && (
              <StatRow icon="😬" label="Worst Score" value={stats.worstScore} />
            )}
            {stats.avgScore != null && (
              <StatRow icon="📊" label="Avg Score" value={stats.avgScore} />
            )}
            {stats.totalStrokes != null && (
              <StatRow icon="🏌️" label="Total Strokes" value={stats.totalStrokes} />
            )}
            {stats.holesPlayed != null && (
              <StatRow icon="🕳️" label="Holes Played" value={stats.holesPlayed} />
            )}
            {stats.avgScorePerHole != null && (
              <StatRow
                icon="📉"
                label="Avg per Hole"
                value={stats.avgScorePerHole}
              />
            )}
            {stats.bestHoleScore != null && (
              <StatRow icon="🎯" label="Best Hole" value={stats.bestHoleScore} />
            )}
            {stats.yellowCards != null && stats.yellowCards > 0 && (
              <StatRow icon="🟨" label="Yellow Cards" value={stats.yellowCards} />
            )}
            {stats.redCards != null && stats.redCards > 0 && (
              <StatRow icon="🟥" label="Red Cards" value={stats.redCards} />
            )}
            {stats.longestStreak != null && stats.longestStreak > 0 && (
              <StatRow
                icon="🔥"
                label="Longest Win Streak"
                value={stats.longestStreak}
              />
            )}
            {stats.favoriteCity && (
              <StatRow icon="🏙️" label="Favorite City" value={stats.favoriteCity} />
            )}
          </div>
        </>
      )}

      <div className="u-label" style={{ marginBottom: '0.75rem' }}>Theme</div>
      <div className="theme-swatches" style={{ marginBottom: '1.5rem' }}>
        {[
          { id: 'clubhouse', label: 'Clubhouse' },
          { id: 'speakeasy', label: 'Speakeasy' },
          { id: 'beach', label: 'Beach' },
        ].map((t) => (
          <button
            key={t.id}
            className={'theme-swatch theme-' + t.id + (theme === t.id ? ' active' : '')}
            onClick={() => setTheme(t.id)}
          >
            <div className="dot" />
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      <div className="u-label" style={{ marginBottom: '0.75rem' }}>Settings</div>
      <div className="settings-list">
        <button className="item" onClick={() => navigate('/games')}>
          <span className="label">Past Games</span>
          <span className="chev">›</span>
        </button>
        <button className="item" onClick={() => navigate('/premium')}>
          <span className="label">Premium</span>
          <span className="chev">›</span>
        </button>
        <button className="item" onClick={() => navigate('/rules')}>
          <span className="label">Rules</span>
          <span className="chev">›</span>
        </button>
        <button className="item" onClick={handleSignOut}>
          <span className="label" style={{ color: 'var(--danger)' }}>
            Sign out
          </span>
          <span className="chev">›</span>
        </button>
      </div>

      <div className="text-center text-dim" style={{ marginTop: '2rem', fontSize: '0.75rem' }}>
        The Social Bar Crawl Experience
      </div>
    </div>
  );
}

function StatRow({ icon, label, value }) {
  return (
    <div className="stat-row">
      <span className="stat-row-icon">{icon}</span>
      <span className="stat-row-label">{label}</span>
      <span className="stat-row-value">{value}</span>
    </div>
  );
}
